import type { Session } from '@ory/client-fetch';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { getSession } from '~/lib/ory/getSession';
import { setOverlay } from '~/stores/auth';
import { BaseUrl } from '~/types/baseUrl';
import { getBaseUrl } from '~/utils/getBaseUrl';
import { logout } from '../utilities/logout';

export const SessionContext = createContext<{
  session: Session | null | undefined;
  isVersori: boolean;
  checkSession: () => Promise<boolean>;
  handleAuthAfterInitialPrompt: ({ initialPrompt, path }: { initialPrompt: string | null; path?: string }) => void;
  handleAuthBackToIntegration: ({
    projectId,
    projectName,
    path,
  }: {
    projectId?: string;
    projectName?: string;
    path?: string;
  }) => void;
  handleLogout: () => void;
  handleLogin: () => void;
  handleSignup: () => void;
}>({
  session: undefined,
  isVersori: false,
  checkSession: async () => false,
  handleLogout: () => {},
  handleAuthAfterInitialPrompt: () => {},
  handleAuthBackToIntegration: () => {},
  handleLogin: () => {},
  handleSignup: () => {},
});

export type SessionContext = typeof SessionContext;

export type SessionProviderProps = {
  children: ReactNode;
};

export const authBaseUrl: BaseUrl = {
  production: 'https://auth.versori.com',
  staging: 'https://auth-staging.versori.com',
  development: 'http://localhost:3030',
};

export const kratosBaseUrl: BaseUrl = {
  production: 'https://auth.versori.com/.idp',
  staging: 'https://auth-staging.versori.com/.idp',
  development: 'http://localhost:4433',
};

export const appBaseUrl: BaseUrl = {
  production: 'https://ai.versori.com',
  staging: 'https://ai-staging.versori.com',
  development: 'http://localhost:5173',
};

export function SessionProvider({ children }: SessionProviderProps) {
  const [session, setSession] = useState<Session | null | undefined>(undefined);
  const isVersori = useMemo(() => {
    return (
      session?.identity?.traits?.email.includes('versori.com') ||
      session?.identity?.traits?.email.includes('versori.io')
    );
  }, [session]);

  const handleAuthBackToIntegration = useCallback(
    ({ projectId, projectName, path = 'register' }: { projectId?: string; projectName?: string; path?: string }) => {
      if (!session) {
        let returnTo = '';
        if (projectId) {
          returnTo = encodeURIComponent(
            `${getBaseUrl(appBaseUrl)}/integrations/${projectId}?projectName=${projectName}`
          );
        } else {
          returnTo = encodeURIComponent(`${getBaseUrl(appBaseUrl)}`);
        }
        window.location.href = `${getBaseUrl(authBaseUrl)}/${path}?return_to=${returnTo}`;
      }
    },
    [session]
  );

  const handleAuthAfterInitialPrompt = useCallback(
    ({ initialPrompt, path = 'register' }: { initialPrompt: string | null; path?: string }) => {
      if (!initialPrompt) {
        return;
      }
      if (!session) {
        const id = JSON.parse(initialPrompt).id;
        const returnTo = encodeURIComponent(`${getBaseUrl(appBaseUrl)}/integrations/${id}`);
        window.location.href = `${getBaseUrl(authBaseUrl)}/${path}?return_to=${returnTo}`;
      }
    },
    [session]
  );

  const handleLogin = useCallback(() => {
    const returnTo = encodeURIComponent(`${getBaseUrl(appBaseUrl)}`);
    window.location.href = `${getBaseUrl(authBaseUrl)}/login?return_to=${returnTo}`;
  }, []);

  const handleSignup = useCallback(() => {
    const returnTo = encodeURIComponent(`${getBaseUrl(appBaseUrl)}`);
    window.location.href = `${getBaseUrl(authBaseUrl)}/register?return_to=${returnTo}`;
  }, []);

  useEffect(() => {
    getSession().then(setSession);
  }, []);

  const checkSession = useCallback(async () => {
    const data = await getSession();
    if (!data) {
      setOverlay('signup');
      return false;
    }
    return true;
  }, []);

  const handleLogout = useCallback(() => {
    const returnTo = getBaseUrl(appBaseUrl);
    logout(getBaseUrl(kratosBaseUrl), returnTo);
  }, []);

  const value = useMemo(() => {
    return {
      session,
      isVersori,
      checkSession,
      handleLogout,
      handleAuthAfterInitialPrompt,
      handleAuthBackToIntegration,
      handleLogin,
      handleSignup,
    };
  }, [
    session,
    isVersori,
    checkSession,
    handleLogout,
    handleAuthAfterInitialPrompt,
    handleAuthBackToIntegration,
    handleLogin,
    handleSignup,
  ]);

  if (session === undefined) {
    return null;
  }

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession() {
  const session = useContext(SessionContext);

  return session;
}
